import { HISTORY_LIMIT, levelOf, loadProfile, rankOf, saveProfile, type Profile } from '../meta/profile';
import { loadSettings, saveSettings } from '../meta/settings';
import { addFriend, fetchEntitlements, fetchLeaderboard, fetchLevelRanking, fetchWeeklyRanking, fetchOpponents, roomServer, type LevelRankRow, type Opponent } from '../net/api';
import { LEVELS, isUnlocked as levelOpen, levelById } from '../sim/campaign/levels';
import { PASS_TIERS, SEASON, passTier, skinsFor, type Reward } from '../sim/content/pass';
import { cachePremium, cachedPremium } from '../meta/entitlements';
import { loadSave } from '../meta/savegame';
import { msToNextWeek, weekId, weeklyLevel } from '../sim/campaign/weekly';
import { weeklyEntry } from '../meta/weeklyRecord';
import { unitDef } from '../sim/units/unitDefs';
import { buildingDef } from '../sim/buildings/definitions';
import { MODES, type ModeId } from '../sim/modes/modes';
import { SAMPLE_BASES } from '../sim/raid/bases';
import { dir, tr } from '../i18n/i18n';
import { icon, type IconId } from './icons';
import { menuSceneSvg } from './menuScene';

/**
 * Main menu (Phases 15–16, DECISIONS D102–D104): plain DOM over an empty page,
 * touch-first (44 px targets), portrait and landscape. Every choice is a URL —
 * `?mode=…`, `?room=…`, `?raid=…` — so a reload, a shared link and the back
 * button all do the expected thing, and the game boots exactly as it does today.
 */
type Screen = 'home' | 'play' | 'campaign' | 'level' | 'weekly' | 'raid' | 'online' | 'profile' | 'leaders' | 'settings' | 'help' | 'pass';

const SOLO: readonly ModeId[] = ['last_colony', 'sandbox', 'classic'];
const SOLO_NAME: Partial<Record<ModeId, string>> = { last_colony: 'Endless Survival', sandbox: 'Sandbox', classic: 'Skirmish' };
const SOLO_TAG: Partial<Record<ModeId, string>> = { last_colony: 'Hordes forever, faster and bigger. How long can you last?', sandbox: 'Build freely, try units and defences.', classic: 'A rival colony played by the computer (normal).' };

let deco = '';
/** Decorative terrain under the campaign road: a river, woods, ruins and nests (seeded, drawn once). */
function mapDecoSvg(): string {
  if (deco) return deco;
  let seed = 7;
  const r = (): number => ((seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0) / 4294967296);
  let woods = '';
  for (let i = 0; i < 26; i++) {
    const cx = r() * 1000;
    const cy = r() * 600;
    for (let k = 0; k < 5; k++) woods += `<circle cx="${(cx + (r() - 0.5) * 60).toFixed(0)}" cy="${(cy + (r() - 0.5) * 40).toFixed(0)}" r="${(8 + r() * 10).toFixed(0)}" fill="${k % 2 ? '#26241f' : '#302d26'}" stroke="#0e0c0a" stroke-width="2"/>`;
  }
  let ruins = '';
  for (let i = 0; i < 14; i++) {
    const x = r() * 960;
    const y = r() * 560;
    ruins += `<g fill="#4a4740" stroke="#1a1814" stroke-width="2"><rect x="${x.toFixed(0)}" y="${y.toFixed(0)}" width="${(16 + r() * 22).toFixed(0)}" height="${(12 + r() * 14).toFixed(0)}"/><rect x="${(x + 20).toFixed(0)}" y="${(y + 14).toFixed(0)}" width="14" height="10"/></g>`;
  }
  deco = `<svg class="camp-deco" viewBox="0 0 1000 600" preserveAspectRatio="none" aria-hidden="true">
    <path d="M-20 120 C160 160 220 60 380 110 S620 260 760 200 960 120 1020 170" fill="none" stroke="#141a19" stroke-width="34" stroke-linecap="round"/>
    <path d="M-20 120 C160 160 220 60 380 110 S620 260 760 200 960 120 1020 170" fill="none" stroke="#2b3634" stroke-width="22" stroke-linecap="round"/>
    <path d="M-20 120 C160 160 220 60 380 110 S620 260 760 200 960 120 1020 170" fill="none" stroke="#46524f" stroke-width="3" stroke-dasharray="10 18"/>
    <g opacity="0.85">${woods}</g><g opacity="0.8">${ruins}</g>
    <g fill="#5b1a1a" stroke="#1a0808" stroke-width="2" opacity="0.7"><ellipse cx="860" cy="80" rx="40" ry="18"/><ellipse cx="140" cy="520" rx="30" ry="12"/><ellipse cx="520" cy="300" rx="26" ry="11"/></g>
  </svg>`;
  return deco;
}

const esc = (s: string): string => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);

export class MainMenu {
  private readonly root: HTMLElement;
  private screen: Screen = 'home';
  private readonly server = roomServer();
  private opponents: Opponent[] | null | 'loading' = null;
  private leaders: { id: string; name: string; points: number; xp: number }[] | null | 'loading' = null;
  private level = 1;
  private levelRanks: { level: number; friends: boolean; rows: LevelRankRow[] | null | 'loading' } | null = null;

  constructor(
    parent: HTMLElement,
    private readonly go: (query: Record<string, string>) => void = (q) => {
      const u = new URL(window.location.href);
      u.search = new URLSearchParams(q).toString();
      window.location.href = u.toString();
    },
  ) {
    this.root = document.createElement('section');
    this.root.className = 'lobby menu';
    this.root.dataset.testid = 'main-menu';
    parent.appendChild(this.root);
    this.root.addEventListener('click', (e) => this.onClick(e));
    this.root.addEventListener('change', (e) => this.onChange(e));
    window.addEventListener('resize', this.onResize);
    this.render();
  }

  private portrait = window.innerWidth < window.innerHeight;
  private readonly onResize = (): void => {
    const portrait = window.innerWidth < window.innerHeight;
    if (portrait === this.portrait) return;
    this.portrait = portrait;
    if (this.screen === 'campaign' || this.screen === 'level') this.render();
  };

  destroy(): void {
    window.removeEventListener('resize', this.onResize);
    this.root.remove();
  }

  private q(extra: Record<string, string>): Record<string, string> {
    const s = new URLSearchParams(window.location.search).get('server');
    return { ...extra, ...(s ? { server: s } : {}) };
  }

  private onClick(e: Event): void {
    const cont = (e.target as HTMLElement).closest<HTMLElement>('[data-continue]');
    if (cont) {
      const q = new URLSearchParams(cont.dataset.continue ?? '');
      const query: Record<string, string> = {};
      q.forEach((v, k) => (query[k] = v));
      this.go({ ...query, resume: '1' });
      return;
    }
    const el = (e.target as HTMLElement).closest<HTMLElement>('[data-go],[data-play],[data-raid],[data-online],[data-level],[data-start-level],[data-start-weekly],[data-ranks],[data-friend],[data-buy]');
    if (!el) return;
    if (el.dataset.level) {
      this.level = Number(el.dataset.level);
      this.screen = 'level';
      if (this.server) this.loadLevelRanks(false);
      this.render();
    } else if (el.dataset.startWeekly) {
      this.go(this.q({ weekly: el.dataset.startWeekly, brief: '1' }));
    } else if (el.dataset.startLevel) {
      this.go(this.q({ level: el.dataset.startLevel, brief: '1' }));
    } else if (el.dataset.ranks) {
      this.loadLevelRanks(el.dataset.ranks === 'friends');
    } else if (el.dataset.friend !== undefined) {
      const code = (this.root.querySelector<HTMLInputElement>('[data-testid="friend-code"]')?.value ?? '').trim().toLowerCase();
      if (!this.server) this.status(tr('Friends need the online server.'));
      else if (!/^[0-9a-f]{16}$/.test(code)) this.status(tr('A player code is 16 letters and digits.'));
      else void addFriend(this.server, code).then((r) => this.status(r.ok ? tr('Added {name}.', { name: r.name ?? tr('your friend') }) : tr('No player with that code.')));
    } else if (el.dataset.buy !== undefined) {
      this.status(tr('Purchases are not connected yet — the Premium track opens once payments are set up.'));
    } else if (el.dataset.go) {
      this.screen = el.dataset.go as Screen;
      if (this.screen === 'raid' && this.server && this.opponents === null) this.loadOpponents();
      if (this.screen === 'leaders' && this.server && this.leaders === null) this.loadLeaders();
      if (this.screen === 'weekly' && this.server) this.loadLevelRanks(false);
      if (this.screen === 'pass' && this.server) void fetchEntitlements(this.server).then((r) => r && (cachePremium(r.premium), this.screen === 'pass' && this.render()));
      this.render();
    } else if (el.dataset.play) {
      this.go(this.q({ mode: el.dataset.play, ...(el.dataset.ai ? { ai: el.dataset.ai } : {}) }));
    } else if (el.dataset.raid) {
      const v = el.dataset.raid;
      if (/^[0-9a-f]{16}$/.test(v)) this.go(this.q({ room: 'new', raid: v }));
      else this.go(this.q({ mode: 'raid', base: v }));
    } else if (el.dataset.online === 'new') {
      this.go(this.q({ room: 'new' }));
    } else if (el.dataset.online === 'join') {
      const code = (this.root.querySelector<HTMLInputElement>('[data-testid="join-code"]')?.value ?? '').trim().toUpperCase();
      if (/^[A-Z]{5}$/.test(code)) this.go(this.q({ room: code }));
      else this.status(tr('Room codes are 5 letters.'));
    }
  }

  private onChange(e: Event): void {
    const el = e.target as HTMLInputElement | HTMLSelectElement;
    switch (el.dataset.setting) {
      case 'name': {
        const name = el.value.replace(/[\u0000-\u001f<>]/g, '').trim().slice(0, 20) || 'Survivor';
        const p: Profile = { ...loadProfile(), name };
        saveProfile(p);
        try {
          localStorage.setItem('atf-name', name);
        } catch {
          /* storage blocked */
        }
        this.status(tr('Name saved.'));
        break;
      }
      case 'volume':
        saveSettings({ volume: Number(el.value) / 100 });
        break;
      case 'sound':
        saveSettings({ sound: (el as HTMLInputElement).checked });
        break;
      case 'skin': {
        const v = el.value;
        const next: Profile = { ...loadProfile() };
        if (v && skinsFor(next.xp, cachedPremium()).some((k) => k.id === v)) (next as { skin?: string }).skin = v;
        else delete (next as { skin?: string }).skin;
        saveProfile(next);
        this.status(tr('Saved.'));
        break;
      }
      case 'music':
        saveSettings({ music: (el as HTMLInputElement).checked });
        break;
      case 'musicVolume':
        saveSettings({ musicVolume: Number(el.value) / 100 });
        break;
      case 'vibration':
        saveSettings({ vibration: (el as HTMLInputElement).checked });
        break;
      case 'quality':
        saveSettings({ quality: el.value === 'low' ? 'low' : 'high' });
        break;
      case 'language':
        saveSettings({ language: el.value === 'ar' ? 'ar' : 'en' });
        this.render();
        break;
      default:
        break;
    }
  }

  private status(text: string): void {
    const s = this.root.querySelector<HTMLElement>('[data-testid="menu-status"]');
    if (s) s.textContent = text;
  }

  private loadOpponents(): void {
    if (!this.server) return;
    this.opponents = 'loading';
    void fetchOpponents(this.server).then((list) => {
      this.opponents = list;
      if (this.screen === 'raid') this.render();
    });
  }

  private loadLeaders(): void {
    if (!this.server) return;
    this.leaders = 'loading';
    void fetchLeaderboard(this.server).then((list) => {
      this.leaders = list;
      if (this.screen === 'leaders') this.render();
    });
  }

  private loadLevelRanks(friends: boolean): void {
    if (!this.server) return;
    const level = this.level;
    this.levelRanks = { level, friends, rows: 'loading' };
    this.render();
    const weekly = this.screen === 'weekly';
    const fetch = weekly ? fetchWeeklyRanking(this.server, weekId(Date.now()), friends) : fetchLevelRanking(this.server, level, friends);
    void fetch.then((rows) => {
      if (this.levelRanks?.level === level && this.levelRanks.friends === friends) this.levelRanks = { level, friends, rows };
      if (this.screen === 'level' || this.screen === 'weekly') this.render();
    });
  }

  private rewardText(r: Reward | undefined): string {
    if (!r) return '—';
    if (r.kind === 'unlock') {
      const name = r.content === 'flame_tower' ? buildingDef('flame_tower').name : unitDef(r.content).name;
      return `🔓 ${tr(name)}`;
    }
    return r.kind === 'skin' ? `🎨 ${esc(tr(r.name))}` : `🏅 ${esc(tr(r.name))}`;
  }

  private weekLeft(): string {
    const ms = msToNextWeek(Date.now());
    const d = Math.floor(ms / 86_400_000);
    const h = Math.floor((ms % 86_400_000) / 3_600_000);
    return d > 0 ? tr('{d}d {h}h left', { d, h }) : tr('{h}h left', { h });
  }

  private rankListHtml(p: Profile): string {
    const ranks = this.levelRanks;
    if (!this.server) return `<p class="menu-note">${tr('Online rankings appear once the server is deployed.')}</p>`;
    if (!ranks || ranks.rows === 'loading') return `<p class="menu-note">${tr('Loading rankings…')}</p>`;
    if (!ranks.rows?.length) return `<p class="menu-note">${ranks.friends ? tr('None of your friends has a score here yet.') : tr('No scores yet — be the first.')}</p>`;
    return `<ol class="lobby-seats" data-testid="level-ranks">${ranks.rows.map((r, i) => `<li class="${r.id === p.server?.id ? 'is-me' : ''}"><span>${i + 1}.</span><span class="seat-name">${esc(r.name)}</span><span>${'★'.repeat(r.stars)} ${r.score.toLocaleString('en')}</span></li>`).join('')}</ol>
      <p class="muted small">${tr("Scores are reported by each player's device.")}</p>`;
  }

  private back(to: Screen = 'home'): string {
    return `<button class="icon-btn menu-back" data-go="${to}" data-testid="menu-back" aria-label="${tr('← Back')}">${icon('back')}</button>`;
  }

  /** A round-cornered icon button with its label under it (side rails on the home screen). */
  private railBtn(go: Screen, id: string, ic: IconId, label: string, badge = ''): string {
    return `<button class="rail-btn" data-go="${go}" data-testid="${id}">${icon(ic)}<span class="rail-lbl">${label}</span>${badge ? `<span class="rail-badge">${badge}</span>` : ''}</button>`;
  }

  /** Campaign map: the 20 levels along a winding road (serpentine grid). */
  private mapHtml(p: Profile): string {
    const prog = p.campaign ?? {};
    const cols = window.innerWidth < window.innerHeight ? 4 : 5;
    const rows = Math.ceil(LEVELS.length / cols);
    const pos = LEVELS.map((_, i) => {
      const row = Math.floor(i / cols);
      const c = i % cols;
      const col = row % 2 === 0 ? c : cols - 1 - c;
      const wob = ((i * 37) % 7) - 3;
      return { x: 9 + (col * 82) / (cols - 1), y: 100 - (14 + (row * 72) / Math.max(1, rows - 1)) + wob * 0.6 };
    });
    const path = pos.map((q, i) => `${i ? 'L' : 'M'}${q.x.toFixed(1)} ${q.y.toFixed(1)}`).join(' ');
    let reached = 0;
    LEVELS.forEach((l, i) => {
      if (levelOpen(l.id, prog)) reached = i;
    });
    const donePath = pos.slice(0, reached + 1).map((q, i) => `${i ? 'L' : 'M'}${q.x.toFixed(1)} ${q.y.toFixed(1)}`).join(' ');
    const nodes = LEVELS.map((l, i) => {
      const open = levelOpen(l.id, prog);
      const best = prog[String(l.id)];
      const q = pos[i]!;
      const kind = !l.goal ? 'survive' : l.goal.kind;
      const cur = open && !best;
      return `<button class="map-node level-card is-${kind}${open ? '' : ' is-locked'}${cur ? ' is-current' : ''}${best ? ' is-done' : ''}" style="left:${q.x}%;top:${q.y}%" ${open ? `data-level="${l.id}"` : 'disabled'} data-testid="level-${l.id}" title="${esc(tr(l.name))}">
        <span class="node-disc"><b>${l.id}</b></span>
        <span class="node-stars">${best ? `${'★'.repeat(best.stars)}${'☆'.repeat(3 - best.stars)}` : open ? '☆☆☆' : '🔒'}</span>
        <span class="node-name">${esc(tr(l.name))}${best ? ` · ${best.score.toLocaleString('en')}` : ''}</span>
        ${cur ? `<span class="node-pin">${icon('pin')}</span>` : ''}
      </button>`;
    }).join('');
    return `<div class="camp-map" data-testid="campaign-map">${mapDecoSvg()}
      <svg class="camp-road" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
        <path d="${path}" class="road-bed"/><path d="${path}" class="road-dash"/><path d="${donePath}" class="road-done"/>
      </svg>${nodes}</div>`;
  }

  private render(): void {
    const p = loadProfile();
    const lv = levelOf(p.xp);
    let body = '';
    switch (this.screen) {
      case 'home': {
        const sv = loadSave();
        const prog = p.campaign ?? {};
        const nextLevel = LEVELS.find((l) => levelOpen(l.id, prog) && !prog[String(l.id)]) ?? LEVELS[LEVELS.length - 1]!;
        body = `
          <div class="home">
            <div class="home-logo">
              <h1 class="lobby-title"><img class="menu-logo" src="${import.meta.env.BASE_URL}brand/logo.webp" alt="After the Fall" width="900" height="556" decoding="async"></h1>
              <p class="menu-tag">${tr('Build. Survive. Command.')}</p>
            </div>
            <nav class="home-rail home-left" aria-label="Events">
              ${this.railBtn('weekly', 'menu-weekly', 'calendar', tr('Weekly Challenge'), esc(this.weekLeft()))}
              ${this.railBtn('leaders', 'menu-leaders', 'trophy', tr('Rankings'))}
              ${this.railBtn('pass', 'menu-pass', 'ticket', tr('Season Pass'))}
            </nav>
            <nav class="home-rail home-right" aria-label="Options">
              ${this.railBtn('settings', 'menu-settings', 'gear', tr('Settings'))}
              ${this.railBtn('help', 'menu-help', 'book', tr('How to play'))}
            </nav>
            <nav class="home-dock" aria-label="Modes">
              <button class="dock-btn" data-go="play" data-testid="menu-play">${icon('skull')}<span>${tr('Survival & Sandbox')}</span></button>
              <button class="dock-btn" data-go="online" data-testid="menu-online">${icon('swords')}<span>${tr('Multiplayer')}</span></button>
              <button class="dock-btn" data-go="raid" data-testid="menu-raid">${icon('crosshair')}<span>${tr('Raid')}</span></button>
            </nav>
            <div class="home-cta">
              ${sv ? (() => {
                const t = Math.floor(sv.tick / 10);
                return `<button class="btn continue-btn" data-continue="${esc(sv.query)}" data-testid="menu-continue">${tr('Continue')} · ${esc(sv.label)} · ${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}</button>`;
              })() : ''}
              <span class="cta-stage">${tr('Level {n}', { n: nextLevel.id })} · ${esc(tr(nextLevel.name))}</span>
              <button class="btn primary play-cta" data-go="campaign" data-testid="menu-campaign">${tr('Campaign')}</button>
            </div>
          </div>`;
        break;
      }
      case 'play':
        body = `<h2 class="menu-h">${tr('Survival & Sandbox')}</h2>
          <div class="menu-cards">${SOLO.map((m) => `
            <button class="menu-card" data-play="${m}" data-testid="play-${m}">
              <strong>${tr(SOLO_NAME[m] ?? MODES[m].name)}</strong><span>${esc(tr(SOLO_TAG[m] ?? MODES[m].tagline))}</span>
            </button>`).join('')}
          </div>
          <h3 class="menu-sub">${tr('Against the computer')}</h3>
          <p class="menu-note">${tr('A rival colony that builds, trains an army, defends and attacks you. Infected roam the middle.')}</p>
          <div class="lobby-actions">${(['easy', 'normal', 'hard'] as const)
            .map((d) => `<button class="btn" data-play="classic" data-ai="${d}" data-testid="vs-ai-${d}">${tr(d[0]!.toUpperCase() + d.slice(1))}</button>`)
            .join('')}</div>
          <div class="lobby-actions"><button class="btn" data-play="duel" data-ai="normal" data-testid="vs-ai-duel">${tr('Duel — no infected')}</button></div>`;
        break;
      case 'campaign': {
        const prog = p.campaign ?? {};
        const total = Object.values(prog).reduce((a, v) => a + v.stars, 0);
        body = `<h2 class="menu-h screen-title">${tr('Campaign')}</h2>
          <p class="menu-note camp-sub" data-testid="campaign-stars">${total} / ${LEVELS.length * 3} ★ · ${tr('each level is harder than the last.')}</p>
          <button class="btn tutorial-card" data-start-level="0" data-testid="level-0">${icon('book')} ${tr('Tutorial')} · ${tr('Training Grounds')}</button>
          ${this.mapHtml(p)}`;
        break;
      }
      case 'level': {
        const l = levelById(this.level) ?? LEVELS[0]!;
        const best = p.campaign?.[String(l.id)];
        const ranks = this.levelRanks;
        const rankList = !this.server
          ? `<p class="menu-note">${tr('Online rankings appear once the server is deployed.')}</p>`
          : !ranks || ranks.rows === 'loading'
            ? `<p class="menu-note">${tr('Loading rankings…')}</p>`
            : !ranks.rows?.length
              ? `<p class="menu-note">${ranks.friends ? tr('None of your friends has a score here yet.') : tr('No scores yet — be the first.')}</p>`
              : `<ol class="lobby-seats" data-testid="level-ranks">${ranks.rows.map((r, i) => `<li class="${r.id === p.server?.id ? 'is-me' : ''}"><span>${i + 1}.</span><span class="seat-name">${esc(r.name)}</span><span>${'★'.repeat(r.stars)} ${r.score.toLocaleString('en')}</span></li>`).join('')}</ol>
                 <p class="muted small">${tr("Scores are reported by each player's device.")}</p>`;
        body = `${this.mapHtml(p)}<div class="menu-modal"><div class="parchment level-sheet">
          <button class="icon-btn close-x" data-go="campaign" aria-label="Close">${icon('close')}</button>
          <h2 class="menu-h ribbon">${tr('Level {n}', { n: l.id })} · ${esc(tr(l.name))}</h2>
          ${l.story ? `<p class="menu-story" data-testid="level-story">${esc(tr(l.story))}</p>` : ''}
          <p class="menu-note">${esc(tr(l.brief))}</p>
          <p data-testid="level-goal">${!l.goal ? tr('Goal: survive {w} hordes, then hold {s} s', { w: l.waves, s: l.holdSeconds }) : l.goal.kind === 'nests' ? tr('Goal: destroy {n} infected nests', { n: l.goal.count }) : tr('Goal: reach {n} survivor camps with any unit', { n: l.goal.camps })} · ${'☠'.repeat(l.difficulty)}</p>
          <p class="menu-note">${best ? `${tr('Your best:')} ${'★'.repeat(best.stars)}${'☆'.repeat(3 - best.stars)} · ${best.score.toLocaleString('en')}` : tr('★ win · ★ lose 3 buildings or fewer · ★ Command Center above 75 %')}</p>
          <button class="btn primary" data-start-level="${l.id}" data-testid="start-level">${tr('Start level {n}', { n: l.id })}</button>
          <h3 class="menu-sub">${tr('Rankings')}</h3>
          ${this.server ? `<div class="lobby-actions"><button class="btn small" data-ranks="all" aria-pressed="${!ranks?.friends}">${tr('Everyone')}</button><button class="btn small" data-ranks="friends" aria-pressed="${!!ranks?.friends}" data-testid="ranks-friends">${tr('Friends')}</button></div>` : ''}
          ${rankList}</div></div>`;
        break;
      }
      case 'weekly': {
        const week = weekId(Date.now());
        const l = weeklyLevel(week);
        const mine = weeklyEntry(week);
        const ranks = this.levelRanks;
        body = `<h2 class="menu-h">${tr('Weekly Challenge')}</h2>
          <p data-testid="weekly-name"><b>${esc(week)}</b> · ${esc(l.name.split(' · ')[1] ?? '')} · ${this.weekLeft()}</p>
          <p class="menu-note">${esc(tr(l.brief))} ${tr('Everyone plays the same map this week. Your first finished run is your score; later runs are practice.')}</p>
          <p>${tr('Goal: survive {w} hordes, then hold {s} s', { w: l.waves, s: l.holdSeconds })}</p>
          <p class="menu-note" data-testid="weekly-mine">${mine ? `${tr('Your score:')} ${'★'.repeat(mine.stars)} ${mine.score.toLocaleString('en')} · ${tr('{n} runs', { n: mine.attempts })}` : tr('Not played yet this week.')}</p>
          <button class="btn primary" data-start-weekly="${esc(week)}" data-testid="start-weekly">${mine ? tr('Practice') : tr('Play this week’s challenge')}</button>
          <h3 class="menu-sub">${tr('Rankings')}</h3>
          ${this.server ? `<div class="lobby-actions"><button class="btn small" data-ranks="all" aria-pressed="${!ranks?.friends}">${tr('Everyone')}</button><button class="btn small" data-ranks="friends" aria-pressed="${!!ranks?.friends}">${tr('Friends')}</button></div>` : ''}
          ${this.rankListHtml(p)}`;
        break;
      }
      case 'pass': {
        const t = passTier(p.xp);
        const premium = cachedPremium();
        body = `<h2 class="menu-h">${esc(tr(SEASON.name))}</h2>
          <p data-testid="pass-tier">${tr('Tier')} ${t.tier} / ${SEASON.tiers}${t.next ? ` · ${tr('{a} / {b} XP to the next tier', { a: t.into, b: t.next })}` : ` · ${tr('complete')}`}</p>
          <div class="xp-bar"><span style="width:${t.next ? Math.round((t.into / t.next) * 100) : 100}%"></span></div>
          <p class="menu-note">${tr('Earn XP in every mode. Units and buildings on the Premium track also unlock for everyone on the Free track a few tiers later.')}</p>
          <button class="btn ${premium ? '' : 'primary'}" data-buy data-testid="pass-buy" ${premium ? 'disabled' : ''}>${premium ? tr('Premium active') : tr('Get Premium')}</button>
          <table class="pass-table" data-testid="pass-table"><thead><tr><th>${tr('Tier')}</th><th>${tr('Free')}</th><th>${tr('Premium')}</th></tr></thead><tbody>
          ${PASS_TIERS.filter((x) => x.free || x.premium).map((x) => `<tr class="${x.tier <= t.tier ? 'is-reached' : ''}"><td>${x.tier}</td><td>${this.rewardText(x.free)}</td><td class="${premium ? '' : 'dim'}">${this.rewardText(x.premium)}</td></tr>`).join('')}
          </tbody></table>`;
        break;
      }
      case 'raid': {
        const online = !this.server
          ? `<p class="menu-note">${tr('Online raids need the room server (not deployed yet). The practice bases below work offline.')}</p>`
          : this.opponents === 'loading'
            ? `<p class="menu-note" data-testid="opponents-loading">${tr('Looking for opponents…')}</p>`
            : !this.opponents?.length
              ? `<p class="menu-note" data-testid="opponents-empty">${tr('No other bases to raid yet — save yours from the Colony panel so others can find it.')}</p>`
              : `<ul class="lobby-seats" data-testid="opponents">${this.opponents
                  .map((o) => `<li><span class="seat-name">${esc(o.name)}</span><span>${o.points} pts · ${o.buildings} bld · ${o.defenders} def</span><button class="btn small" data-raid="${o.id}">${tr('Raid')}</button></li>`)
                  .join('')}</ul>`;
        body = `<h2 class="menu-h">${tr('Raid')}</h2>
          <p class="menu-note">${tr('Your army: 8 Rangers, 4 Heavies, 1 Engineer. Destroy the Command Center before the timer for stars, loot and ranking points.')}</p>
          <h3 class="menu-sub">${tr('Opponents')}</h3>${online}
          <h3 class="menu-sub">${tr('Practice')}</h3>
          <div class="menu-cards">${SAMPLE_BASES.map((b, i) => `
            <button class="menu-card" data-raid="${i}" data-testid="raid-sample-${i}"><strong>${esc(b.name)}</strong><span>${tr('{b} buildings · {d} defenders', { b: b.buildings.length, d: b.garrison.length })}</span></button>`).join('')}
            ${p.base ? `<button class="menu-card" data-raid="mine" data-testid="raid-mine"><strong>${tr('Your own base')}</strong><span>${tr('Test your defences')}</span></button>` : ''}
          </div>`;
        break;
      }
      case 'online':
        body = `<h2 class="menu-h">${tr('Multiplayer')}</h2>
          ${this.server
            ? `<button class="btn primary" data-online="new" data-testid="online-new">${tr('Create a room')}</button>
               <div class="lobby-row"><label>${tr('Room code')}<input class="menu-input" data-testid="join-code" maxlength="5" autocapitalize="characters" autocomplete="off" placeholder="ABCDE"></label>
               <button class="btn" data-online="join" data-testid="online-join">${tr('Join')}</button></div>
               <ul class="menu-help">
                 <li>${tr("<b>Last Colony</b> (2–4): the infected hit everyone. Break your friends' colonies while the hordes wear them down — build a <i>Horde Beacon</i> to send the next horde at a rival.")}</li>
                 <li>${tr('<b>Duel</b> (1 v 1): you against a friend, no infected.')}</li>
                 <li>${tr('<b>Classic RTS</b> (2–4, FFA or 2 v 2): bases, armies, a few infected in the wild.')}</li>
               </ul>
               <p class="menu-note">${tr('The host picks the mode in the lobby. Share the invite link.')}</p>`
            : `<p class="menu-note" data-testid="online-off">${tr('Online play needs the room server, which is not deployed yet.')}</p>`}`;
        break;
      case 'profile': {
        const wins = p.history.filter((h) => h.result === 'victory').length;
        body = `<h2 class="menu-h">${tr('Profile')}</h2>
          <div class="lobby-row"><label>${tr('Name')}<input class="menu-input" data-setting="name" data-testid="profile-name" maxlength="20" value="${esc(p.name)}"></label></div>
          ${(() => {
            const skins = skinsFor(p.xp, cachedPremium());
            return `<div class="lobby-row"><label>${tr('Colony look')}<select data-setting="skin" data-testid="profile-skin">
              <option value="">${tr('Standard')}</option>
              ${skins.map((k) => `<option value="${k.id}" ${p.skin === k.id ? 'selected' : ''}>${esc(tr(k.name))}</option>`).join('')}
            </select></label></div>${skins.length ? '' : `<p class="muted small">${tr('Earn skins in the Season Pass.')}</p>`}`;
          })()}
          <p data-testid="profile-level">${tr('Level {n}', { n: lv.level })} · ${tr('{a} / {b} XP to the next level', { a: lv.into, b: lv.next })}</p>
          <div class="xp-bar"><span style="width:${Math.round((lv.into / lv.next) * 100)}%"></span></div>
          <p data-testid="profile-rank">${tr(rankOf(p.points))} · ${tr('{p} ranking points · {w} wins in the last {m} matches', { p: p.points, w: wins, m: Math.min(p.history.length, HISTORY_LIMIT) })}</p>
          <h3 class="menu-sub">${tr('History')}</h3>
          ${p.history.length
            ? `<ul class="lobby-seats" data-testid="history">${p.history
                .slice(0, 10)
                .map((h) => `<li><span class="seat-name">${esc(tr(MODES[h.mode as ModeId]?.name ?? h.mode))}${h.opponent ? ` ${tr('vs')} ${esc(h.opponent)}` : ''}</span><span class="seat-state ${h.result === 'victory' ? 'ok' : 'bad'}">${h.result === 'victory' ? tr('Victory') : tr('Defeat')}${h.stars !== undefined ? ` ${'★'.repeat(h.stars)}` : ''}</span><span>+${h.xp} XP</span></li>`)
                .join('')}</ul>`
            : `<p class="menu-note" data-testid="history-empty">${tr('No matches yet.')}</p>`}`;
        break;
      }
      case 'leaders': {
        const list = !this.server
          ? `<p class="menu-note" data-testid="leaders-off">${tr('Rankings are online — they appear once the room server is deployed. Your own points are on your profile.')}</p>`
          : this.leaders === 'loading'
            ? `<p class="menu-note">${tr('Loading…')}</p>`
            : !this.leaders?.length
              ? `<p class="menu-note">${tr('No ranked players yet.')}</p>`
              : `<ol class="lobby-seats" data-testid="leaders">${this.leaders
                  .map((r, i) => `<li class="${r.id === p.server?.id ? 'is-me' : ''}"><span>${i + 1}.</span><span class="seat-name">${esc(r.name)}</span><span>${tr('{n} pts', { n: r.points })} · ${tr(rankOf(r.points))}</span></li>`)
                  .join('')}</ol>`;
        const friends = this.server
          ? `<h3 class="menu-sub">${tr('Friends')}</h3>
             <p class="menu-note">${tr('Your player code:')} <code data-testid="my-code">${esc(p.server?.id ?? tr('appears after you go online once'))}</code></p>
             <div class="lobby-row"><label>${tr("Friend's code")}<input class="menu-input" data-testid="friend-code" maxlength="16" autocomplete="off"></label><button class="btn" data-friend data-testid="add-friend">${tr('Add')}</button></div>`
          : '';
        body = `<h2 class="menu-h">${tr('Rankings')}</h2>${list}${friends}`;
        break;
      }
      case 'settings': {
        const s = loadSettings();
        body = `<h2 class="menu-h">${tr('Settings')}</h2>
          <div class="lobby-row"><label>Language · اللغة<select data-setting="language" data-testid="set-language">
            <option value="en" ${s.language === 'en' ? 'selected' : ''}>English</option>
            <option value="ar" ${s.language === 'ar' ? 'selected' : ''}>العربية</option>
          </select></label></div>
          <label class="menu-check"><input type="checkbox" data-setting="sound" data-testid="set-sound" ${s.sound ? 'checked' : ''}> ${tr('Sound effects')}</label>
          <label class="menu-range">${tr('Volume')} <input type="range" min="0" max="100" step="5" data-setting="volume" data-testid="set-volume" value="${Math.round(s.volume * 100)}"></label>
          <label class="menu-check"><input type="checkbox" data-setting="music" data-testid="set-music" ${s.music ? 'checked' : ''}> ${tr('Music')}</label>
          <label class="menu-range">${tr('Music volume')} <input type="range" min="0" max="100" step="5" data-setting="musicVolume" data-testid="set-music-volume" value="${Math.round(s.musicVolume * 100)}"></label>
          <label class="menu-check"><input type="checkbox" data-setting="vibration" data-testid="set-vibration" ${s.vibration ? 'checked' : ''}> ${tr('Vibration (phones)')}</label>
          <div class="lobby-row"><label>${tr('Graphics')}<select data-setting="quality" data-testid="set-quality">
            <option value="high" ${s.quality === 'high' ? 'selected' : ''}>${tr('High')}</option>
            <option value="low" ${s.quality === 'low' ? 'selected' : ''}>${tr('Low (older phones)')}</option>
          </select></label></div>`;
        break;
      }
      case 'help':
        body = `<h2 class="menu-h">${tr('How to play')}</h2>
          <ul class="menu-help">
            <li>${tr('<b>Build</b> from the bar at the bottom: tap a building, tap the map, confirm. Workers come from your survivors.')}</li>
            <li>${tr('<b>Economy</b>: Farms feed survivors, Sawmills and Scrap Yards gather; Warehouses store. Housing lets more survivors join.')}</li>
            <li>${tr('<b>Army</b>: train Rangers, Heavies and Engineers at the Barracks. Tap <i>Army</i> to select everyone, tap the ground to move, <i>Attack-move</i> to fight on the way.')}</li>
            <li>${tr('<b>Camera</b>: drag with one finger to pan, pinch to zoom. On a computer: drag, WASD / arrows, mouse wheel.')}</li>
            <li>${tr('<b>Infected</b>: bites infect; an infected survivor dies and rises as a Walker. Walls, Gates and Gun Towers (needs a Generator) hold hordes.')}</li>
            <li>${tr('<b>Campaign</b>: hold out until the last horde of the level is spent. Stars for losing little; each level is harder.')}</li>
            <li>${tr('<b>Win online</b>: destroy every rival Command Center. Lose yours and you are out.')}</li>
          </ul>`;
        break;
    }
    const prog = p.campaign ?? {};
    const stars = Object.values(prog).reduce((a, v) => a + v.stars, 0);
    const tier = passTier(p.xp).tier;
    const top = this.screen === 'home'
      ? `<button class="menu-profile" data-go="profile" data-testid="menu-profile">
           <span class="avatar">${icon('user')}<b class="lvl">${lv.level}</b></span>
           <span class="who"><strong>${esc(p.name)}</strong><span>${tr('Level {n}', { n: lv.level })} · ${tr(rankOf(p.points))} · ${tr('{n} pts', { n: p.points })}</span></span>
         </button>`
      : `<div class="nav-btns">${this.back(this.screen === 'level' ? 'campaign' : 'home')}${this.screen === 'level' ? `<button class="icon-btn" data-go="home" aria-label="Home">${icon('home')}</button>` : ''}</div>`;
    const wallet = `<div class="menu-wallet" aria-label="Progress">
        <span class="coin" title="${tr('Campaign')}">${icon('star')}<b>${stars}</b></span>
        <span class="coin" title="${tr('Season Pass')}">${icon('ticket')}<b>${tier}</b></span>
        <span class="coin" title="${tr('Rankings')}">${icon('trophy')}<b>${p.points}</b></span>
      </div>`;
    const panel = this.screen === 'home' || this.screen === 'campaign' || this.screen === 'level';
    this.root.innerHTML = `<div class="menu-scene">${menuSceneSvg()}</div>
      <div class="menu-shell menu-card-wrap" dir="${dir()}" lang="${dir() === 'rtl' ? 'ar' : 'en'}" data-screen="${this.screen}">
        <header class="menu-top">${top}${wallet}</header>
        <main class="menu-main">${panel ? body : `<div class="menu-panel">${body}</div>`}</main>
        <p class="lobby-status" data-testid="menu-status" aria-live="polite"></p>
      </div>`;
    if (this.screen === 'campaign') this.root.querySelector('.map-node.is-current')?.scrollIntoView({ block: 'center' });
  }
}
